import React, { useEffect, useState, useCallback } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import LoopIcon from '@mui/icons-material/Loop';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import CloseIcon from '@mui/icons-material/Close';
import StorageIcon from '@mui/icons-material/Storage';
import { keyframes } from '@mui/system';
import { apiServer } from '../../../api';
import { IHistoricoTarefaAgendada, IEtapaHistorico } from '../../../types/historicoTarefaAgendada';
import { useLogin } from '../../../contexts/LoginContext';
import { formatarDataHoraCurta } from '../../../common/utils/formatadorDataHora';

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export interface IProps {
    sizeTable: "small" | "medium";
    limit: number;
}

function obterIconeSituacao(status: string) {
    const s = status ? status.toLowerCase() : '';
    if (s.includes('salvolocalmente') || s.includes('salvo localmente') || s.includes('salvo_localmente')) {
        return (
            <Tooltip title="Backup concluído e protegido no servidor local. Sincronização em nuvem pendente de envio.">
                <Chip
                    icon={<StorageIcon style={{ color: '#0288d1' }} />}
                    label="Salvo Localmente"
                    variant="outlined"
                    color="info"
                    size="small"
                    sx={{ 
                        fontWeight: 'bold',
                        boxShadow: '0 2px 5px rgba(2, 136, 209, 0.15)',
                        borderColor: 'info.main'
                    }}
                />
            </Tooltip>
        );
    }
    if (s === 'executado' || s === 'executada' || s === 'concluido' || s === 'concluído' || s === 'sucesso') {
        const labelText = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
        return (
            <Chip
                icon={<CheckCircleIcon style={{ color: '#2e7d32' }} />}
                label={labelText}
                variant="outlined"
                color="success"
                size="small"
                sx={{ 
                    fontWeight: 'bold',
                    boxShadow: '0 2px 5px rgba(46, 125, 50, 0.15)',
                    borderColor: 'success.main'
                }}
            />
        );
    }
    if (s === 'falha' || s === 'erro') {
        const labelText = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
        return (
            <Chip
                icon={<ErrorIcon style={{ color: '#d32f2f' }} />}
                label={labelText}
                variant="outlined"
                color="error"
                size="small"
                sx={{ 
                    fontWeight: 'bold',
                    boxShadow: '0 2px 5px rgba(211, 47, 47, 0.15)',
                    borderColor: 'error.main'
                }}
            />
        );
    }
    if (s === 'aviso' || s === 'atencao' || s === 'atenção') {
        const labelText = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
        return (
            <Chip
                icon={<WarningIcon style={{ color: '#ed6c02' }} />}
                label={labelText}
                variant="outlined"
                color="warning"
                size="small"
                sx={{ 
                    fontWeight: 'bold',
                    boxShadow: '0 2px 5px rgba(237, 108, 2, 0.15)',
                    borderColor: 'warning.main'
                }}
            />
        );
    }
    if (s === 'processando' || s === 'executando' || s === 'em_andamento') {
        const labelText = (status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()).replace('_', ' ');
        return (
            <Chip
                icon={<LoopIcon sx={{ animation: `${spin} 2s linear infinite`, color: '#0288d1' }} />}
                label={labelText}
                variant="outlined"
                color="info"
                size="small"
                sx={{ 
                    fontWeight: 'bold',
                    boxShadow: '0 2px 5px rgba(2, 136, 209, 0.15)',
                    borderColor: 'info.main'
                }}
            />
        );
    }
    // Fallback
    return (
        <Chip
            label={status || 'Desconhecido'}
            variant="outlined"
            size="small"
        />
    );
}

function formatarTempoHumanizado(segundosTotais: number): string {
    if (!segundosTotais || segundosTotais <= 0) return '0 s';
    const s = Math.round(segundosTotais);
    if (s < 60) return `${s} s`;
    
    const horas = Math.floor(s / 3600);
    const minutos = Math.floor((s % 3600) / 60);
    const segsRestantes = s % 60;

    if (horas > 0) {
        if (minutos === 0 && segsRestantes === 0) return `${horas} h`;
        if (segsRestantes === 0) return `${horas} h ${minutos} min`;
        return `${horas} h ${minutos} min ${segsRestantes} s`;
    }

    if (segsRestantes === 0) return `${minutos} min`;
    return `${minutos} min ${segsRestantes} s`;
}

function formatarDescricaoEtapa(descricao: string): string {
    if (!descricao) return '';
    return descricao.replace(/^Etapa\s+\d+\s+(Base\.\s+|SQL Server\.\s+|Postgres\.\s+|Sybase\.\s+|CheckDB\.\s+)?/i, '').trim();
}

export default function GradeHistoricoRecente(props: IProps) {
    const [login] = useLogin();
    const [historicos, setHistoricos] = useState<IHistoricoTarefaAgendada[]>([]);
    const [historicoSelecionado, setHistoricoSelecionado] = useState<IHistoricoTarefaAgendada | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const carregarHistoricos = useCallback(() => {
        const clienteID = login.usuario?.usuarioCliente?.clienteID;
        if (!clienteID || !login.accessToken)
            return;

        apiServer<IHistoricoTarefaAgendada[]>()
            .get(`/historico-tarefa-agendada/${clienteID}/${props.limit}`, login.accessToken)
            .then((response) => {
                setHistoricos(response.data || []);
            })
            .catch(() => {});
    }, [login.accessToken, props.limit, login.usuario?.usuarioCliente?.clienteID]);

    useEffect(() => {
        carregarHistoricos();
    }, [carregarHistoricos]);

    const etapasDoHistorico: IEtapaHistorico[] = React.useMemo(() => {
        if (!historicoSelecionado?.etapas) return [];
        if (typeof historicoSelecionado.etapas === 'string') {
            try {
                return JSON.parse(historicoSelecionado.etapas as unknown as string);
            } catch {
                return [];
            }
        }
        return Array.isArray(historicoSelecionado.etapas) ? historicoSelecionado.etapas : [];
    }, [historicoSelecionado]);

    const tempoTotalFormatado = React.useMemo(() => {
        if (!historicoSelecionado) return '0 s';
        let totalSegundos = 0;
        if (historicoSelecionado.duracao && historicoSelecionado.duracao > 0) {
            totalSegundos = historicoSelecionado.duracao;
        } else if (etapasDoHistorico.length > 0) {
            totalSegundos = etapasDoHistorico.reduce((acc, curr) => acc + (curr.duracaoSegundos || 0), 0);
        } else if (historicoSelecionado.dataHoraInicioExecucao && historicoSelecionado.dataHoraFimExecucao) {
            const inicio = new Date(historicoSelecionado.dataHoraInicioExecucao).getTime();
            const fim = new Date(historicoSelecionado.dataHoraFimExecucao).getTime();
            if (fim > inicio) {
                totalSegundos = Math.round((fim - inicio) / 1000);
            }
        }
        return formatarTempoHumanizado(totalSegundos);
    }, [historicoSelecionado, etapasDoHistorico]);

    const obterTamanhoMB = (row: IHistoricoTarefaAgendada): number => {
        if (row.arquivo && row.arquivo.tamanho > 0) {
            return Math.round(row.arquivo.tamanho);
        }
        const local = row.tamanhoLocalMB || row.tamanho_local_mb;
        if (local && local > 0) {
            return Math.round(local);
        }
        return 0;
    };

    return (
        <>
            {isMobile ? (
                historicos.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="text.secondary">Nenhum histórico recente encontrado.</Typography>
                    </Box>
                ) : (
                    <Stack spacing={2} sx={{ p: 1 }}>
                        {historicos.map((row, i) => (
                            <Card 
                                key={i} 
                                variant="outlined" 
                                onClick={() => setHistoricoSelecionado(row)}
                                sx={{ 
                                    borderRadius: 2, 
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        borderColor: 'primary.main'
                                    }
                                }}
                            >
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                            {row.tarefaAgendada?.nome || 'Tarefa'}
                                        </Typography>
                                        {obterIconeSituacao(row.status)}
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Data / Hora
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {formatarDataHoraCurta(row.dataCadastro || row.dataHoraInicioExecucao)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Origem
                                            </Typography>
                                            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                                {row.nomeDispositivo || 'Servidor'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Tamanho do Arquivo
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {obterTamanhoMB(row)} MB{(!row.arquivo || row.arquivo.tamanho === 0) && (row.tamanhoLocalMB || row.tamanho_local_mb) ? ' (Local)' : ''}
                                            </Typography>
                                        </Grid>
                                        {(row.etapaFalha || row.etapa_falha) && (
                                            <Grid item xs={12}>
                                                <Typography variant="caption" color="error.main" sx={{ fontWeight: 'bold' }}>
                                                    Parou em: {row.etapaFalha || row.etapa_falha}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )
            ) : (
                <Table size={props.sizeTable}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Tarefa</TableCell>
                            <TableCell>Origem</TableCell>
                            <TableCell align="right">Tamanho</TableCell>
                            <TableCell>Situação</TableCell>
                            <TableCell align="center">Etapas</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {historicos.map((row, i) => {
                            const falha = row.etapaFalha || row.etapa_falha;
                            return (
                                <TableRow 
                                    key={i} 
                                    hover 
                                    onClick={() => setHistoricoSelecionado(row)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>{formatarDataHoraCurta(row.dataCadastro || row.dataHoraInicioExecucao)}</TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}>
                                        {row.tarefaAgendada?.nome || 'Tarefa'}
                                        {falha && (
                                            <Typography variant="caption" color="error.main" display="block">
                                                Falha: {falha}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{row.nomeDispositivo || 'Servidor'}</TableCell>
                                    <TableCell align="right">
                                        {obterTamanhoMB(row)} MB{(!row.arquivo || row.arquivo.tamanho === 0) && (row.tamanhoLocalMB || row.tamanho_local_mb) ? ' (Local)' : ''}
                                    </TableCell>
                                    <TableCell>{obterIconeSituacao(row.status)}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Ver detalhes das etapas">
                                            <IconButton size="small" color={falha ? "error" : "primary"}>
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}

            {/* Modal Analítico de Etapas e Auditoria */}
            <Dialog
                open={Boolean(historicoSelecionado)}
                onClose={() => setHistoricoSelecionado(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                                Auditoria e Detalhes da Execução
                            </Typography>
                            <Chip
                                icon={<TimerOutlinedIcon style={{ fontSize: 16 }} />}
                                label={`Tempo Total: ${tempoTotalFormatado}`}
                                color="primary"
                                variant="outlined"
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                            />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            {historicoSelecionado?.tarefaAgendada?.nome} — {formatarDataHoraCurta(historicoSelecionado?.dataCadastro || historicoSelecionado?.dataHoraInicioExecucao)}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setHistoricoSelecionado(null)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ p: 2.5 }}>
                    {(historicoSelecionado?.etapaFalha || historicoSelecionado?.etapa_falha) && (
                        <Alert 
                            severity={historicoSelecionado?.status?.toLowerCase().includes('salvo') ? "info" : "error"} 
                            sx={{ mb: 2.5 }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {historicoSelecionado?.status?.toLowerCase().includes('salvo')
                                    ? `Backup Salvo Localmente com Sucesso — Envio em Nuvem Pendente (${historicoSelecionado.etapaFalha || historicoSelecionado.etapa_falha})`
                                    : `Execução Interrompida em: ${historicoSelecionado.etapaFalha || historicoSelecionado.etapa_falha}`}
                            </Typography>
                            {historicoSelecionado.resultado && (
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {historicoSelecionado.resultado}
                                </Typography>
                            )}
                        </Alert>
                    )}

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Progresso Analítico por Etapa
                        </Typography>

                        {etapasDoHistorico.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
                                Detalhamento por etapas não disponível para esta execução legada.
                            </Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                {etapasDoHistorico.map((etapa, idx) => {
                                    const descLimpa = formatarDescricaoEtapa(etapa.descricao);
                                    return (
                                        <Box
                                            key={idx}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1.5,
                                                border: '1px solid',
                                                borderColor: etapa.sucesso ? 'divider' : 'error.light',
                                                bgcolor: etapa.sucesso ? 'background.paper' : 'error.lighter',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: 1
                                            }}
                                        >
                                            <Box display="flex" alignItems="center" gap={1.5}>
                                                {etapa.sucesso ? (
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                ) : (
                                                    <ErrorIcon color="error" fontSize="small" />
                                                )}
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        Etapa {etapa.etapa}: {descLimpa || etapa.descricao}
                                                    </Typography>
                                                    {etapa.observacao && (
                                                        <Typography variant="caption" color="error.main" display="block">
                                                            {etapa.observacao}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>

                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Chip
                                                    icon={<TimerOutlinedIcon style={{ fontSize: 16 }} />}
                                                    label={formatarTempoHumanizado(etapa.duracaoSegundos ?? 0)}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontWeight: 500 }}
                                                />
                                                <Chip
                                                    label={etapa.sucesso ? "Concluída" : "Falhou"}
                                                    size="small"
                                                    color={etapa.sucesso ? "success" : "error"}
                                                />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={2.4}>
                            <Typography variant="caption" color="text.secondary">Tempo Total</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                {tempoTotalFormatado}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={2.4}>
                            <Typography variant="caption" color="text.secondary">Dispositivo</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {historicoSelecionado?.nomeDispositivo || 'Servidor'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={2.4}>
                            <Typography variant="caption" color="text.secondary">Sistema Operacional</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {historicoSelecionado?.sistemaOperacional || 'Windows'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={2.4}>
                            <Typography variant="caption" color="text.secondary">Versão do Agente</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {historicoSelecionado?.versaoCliente || '1.2.5'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={2.4}>
                            <Typography variant="caption" color="text.secondary">Arquivo Gerado</Typography>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                {historicoSelecionado?.nomeArquivoGerado || '-'}
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 2.5, py: 1.5 }}>
                    <Button onClick={() => setHistoricoSelecionado(null)} variant="outlined">
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}